import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";

import TrashOutlined from "@/components/Icons/TrashOutlined.tsx";
import { prettyDate } from "@/components/Search/search.ts";
import { WsTemplateExtended } from "@/components/Templates/templates.ts";
import useTemplates from "@/components/Templates/useTemplates.ts";
import TextInputHelper from "@/components/TextInputHelper.tsx";
import { FullInput } from "@/components/Utils.tsx";
import Locator from "@/locator.ts";
import ConfirmDiscardChanges from "../ModalForms/ConfirmDiscardChanges";

export default function RightPanel({
  template,
  allowTemplateUse,
  newTemplate,
  setActiveTemplate,
  setDisableCloseModal,
  useSelectedTemplate,
  closeModal,
}: {
  template: WsTemplateExtended | null;
  allowTemplateUse: boolean;
  newTemplate: boolean;
  setDisableCloseModal: (value: boolean) => void;
  setActiveTemplate?: (value: WsTemplateExtended) => void;
  useSelectedTemplate?: (value: string) => void;
  closeModal?: (value: boolean) => void;
}) {
  const { cloneTemplate, saveTemplate, deleteTemplate, updateTemplate } = useTemplates();
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>(template?.name || "");
  const [templateText, setTemplateText] = useState<string>(template?.template || "");
  const characterLimit = 5000;
  const hasNameChanges = templateName?.length > 0 && templateName !== template?.name;
  const hasTemplateChanges = templateText?.length > 0 && templateText !== template?.template;
  const hasChanges = hasNameChanges || hasTemplateChanges;

  useEffect(() => {
    setDisableCloseModal(hasChanges);
  }, [hasChanges, setDisableCloseModal]);

  useEffect(() => setError(null), [template]);

  const useThisTemplate = () => {
    useSelectedTemplate?.(templateText);
  };

  const deleteTemplateById = async () => {
    setDisabled(true);
    try {
      await deleteTemplate(template.id);
      setActiveTemplate(null);
      setError(null);
    } catch (error) {
      setError(error?.message ?? "Error");
    } finally {
      setDisabled(false);
    }
  };

  const saveActiveTemplate = async () => {
    setDisabled(() => true);
    try {
      const response = await updateTemplate({ id: template.id, name: templateName, template: templateText });
      setActiveTemplate(response);
    } catch (error) {
      setError(error?.message ?? "Error");
    } finally {
      setDisabled(() => false);
    }
  };
  const saveNewTemplate = async () => {
    setDisabled(() => true);
    try {
      await saveTemplate({ name: templateName, template: templateText });
      closeModal?.(true);
      setError(null);
    } catch (error) {
      setError(error?.message ?? "Error");
    } finally {
      setDisabled(() => false);
    }
  };

  const clone = async () => {
    setDisabled(() => true);
    try {
      const response = await cloneTemplate(template.id);
      setActiveTemplate(response);
    } catch (error) {
      setError(error?.message ?? "Error");
    } finally {
      setDisabled(() => false);
    }
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (template?.canEdit === false) {
      setError("You do not have permission to edit this template.");
      return;
    }
    template?.id ? await saveActiveTemplate() : await saveNewTemplate();
  };

  const onKeyDown = async (e: React.KeyboardEvent) => {
    if (e.metaKey && e.key === "Enter") {
      if (template?.canEdit === false) {
        setError("You do not have permission to edit this template.");
        return;
      }
      template?.id ? await saveActiveTemplate() : await saveNewTemplate();
    }
  };

  return (
    <Box sx={{ height: "100%" }}>
      {(template || newTemplate) && (
        <>
          <Box component="form" onSubmit={(e) => submit(e)}>
            {template?.id && (
              <Box
                sx={{
                  color: theme.palette.secondary.light,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "end",
                  fontSize: "14px",
                }}
              >
                <Box>{template?.authorName}</Box>
                <Box>{prettyDate(template?.updatedAt)}</Box>
              </Box>
            )}

            <FullInput
              id="newTemplateName"
              placeholder="Enter a title"
              autoFocus
              required
              className="input-v2"
              disabled={disabled || template?.canEdit === false}
              sx={{ mb: 2 }}
              value={templateName}
              callback={(e) => setTemplateName(e.target.value)}
              aria-label={Locator.feed.input.templates.modal.selectedTitle}
            />

            <FullInput
              id="activeTemplate"
              className="text-input-v2"
              multiline
              required
              disabled={disabled || template?.canEdit === false}
              placeholder="Start typing your templated message"
              rows={7.6}
              error={templateText?.length > characterLimit}
              inputProps={{
                className: "scrollable-content",
              }}
              sx={{ height: "100%" }}
              formControlProps={{
                sx: { height: "100%" },
              }}
              value={templateText}
              callback={(e) => setTemplateText(e.target.value)}
              onKeyDown={onKeyDown}
              helperText={<TextInputHelper textInput={templateText ?? ""} limit={characterLimit} />}
              aria-label={Locator.feed.input.templates.modal.selectedContent}
            />

            {error && (
              <Box
                sx={{
                  p: 1,
                  mb: 1,
                  mt: "-49px",
                  zIndex: 10,
                  borderRadius: 1,
                  cursor: "pointer",
                  position: "relative",
                  background: theme.palette.error.main,
                }}
                onClick={() => setError(null)}
              >
                {error}
              </Box>
            )}

            <Box sx={{ mb: 2, display: "flex", gap: 4 }}>
              {allowTemplateUse && template?.id && (
                <Box sx={{ flexGrow: 1 }}>
                  <Button onClick={useThisTemplate} variant="contained" disabled={disabled} type="button">
                    Use This Template
                  </Button>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                {(!template?.id || template?.canEdit) && (
                  <Button variant="contained" color="primary" disabled={disabled || !hasChanges} type="submit">
                    Save
                  </Button>
                )}
                {template?.id && (
                  <Tooltip title="Clone Template">
                    <IconButton
                      aria-label="Clone Template"
                      onClick={clone}
                      disabled={disabled || hasChanges}
                      sx={{ width: 44 }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {template?.canEdit && (
                  <Tooltip title="Delete Template">
                    <IconButton
                      aria-label="Delete Template"
                      onClick={() => setConfirmDelete((value) => !value)}
                      disabled={disabled}
                      sx={{ width: 44 }}
                    >
                      <TrashOutlined role="img" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
          <ConfirmDiscardChanges
            id={template?.id}
            visible={confirmDelete}
            label="Are you sure?"
            text="Confirm you would like to delete this template. Your content will be lost."
            deleteText="Delete"
            denyCloseHandler={() => setConfirmDelete(() => false)}
            confirmCloseHandler={() => deleteTemplateById()}
          />
        </>
      )}

      {!template && !newTemplate && (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%", justifyContent: "center" }}>
          Please select a template to view its details.
        </Box>
      )}
    </Box>
  );
}
